import React from 'react';
import { Box, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AutorenewIcon from '@mui/icons-material/Autorenew';

  interface StatusTagProps {
    flagValue?: number | null;
    children: React.ReactNode;
    title?: string;
  }

  const AssetStatusTag: React.FC<StatusTagProps> = ({ flagValue, children, title }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // useMemo hook efficiently calculates the display properties based on the flagValue
    const statusConfig = React.useMemo(() => {
      let icon = null;
      let backgroundColor = theme.palette.status.notStarted;

      switch (flagValue) {
        case 0: // PENDING
          backgroundColor = theme.palette.status.pending;
          break;
        case 100: // COMPLETE
        case 101: // NOT NEEDED
          backgroundColor = theme.palette.status.complete;
          icon = <CheckCircleIcon sx={{ fontSize: 16 }} />;
          break;
        case 300: // ERROR
          backgroundColor = theme.palette.status.error;
          icon = <ErrorIcon sx={{ fontSize: 16 }} />;
          break;
        case 301: // RETRYING
          backgroundColor = theme.palette.status.retrying;
          icon = <AutorenewIcon sx={{ fontSize: 16 }} />;
          break;
        default: // NOT STARTED
          // Uses the initial values
          break;
      }
      return { icon, backgroundColor };
    }, [flagValue, theme]);

    const content = (
      <Box
        component="span"
        sx={{
          backgroundColor: statusConfig.backgroundColor,
          color: theme.palette.getContrastText(statusConfig.backgroundColor),
          borderRadius: isMobile ? '3px' : '12px', // Pill shape
          padding: isMobile ? '2px 6px' : '4px 10px',
          display: 'inline-flex', // Use flexbox to align icon and text
          alignItems: 'center',   // Vertically center content
          gap: 0.5,               // Add space between icon and text
          fontSize: isMobile ? '11px' :'12px',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
        }}
      >
        {statusConfig.icon}
        <span>{children}</span>
      </Box>
    );

    if (!title) return content;

    return (
      <Tooltip title={title} arrow>
        {content}
      </Tooltip>
    );
  };

  

export default AssetStatusTag;
